import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createStation, updateStation, uploadStationLogo } from '@/services/station';

import { useAuth } from '@/contexts/AuthProvider';

import type {
  TCreateStationPayload,
  TStationInformation,
  TUpdateStationPayload,
} from '@/types/station';

import { catalogKeys } from '@/queries/catalog';
import { dashboardKeys } from '@/queries/dashboard';
import { geofenceKeys } from '@/queries/geofence';
import { stationKeys } from '@/queries/station';

/**
 * Cancels the loading station calls before a station save, so an older answer cannot land after the
 * save and overwrite fresh data. Shared by W1 and W2.
 */
const cancelStationQueries = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.cancelQueries(stationKeys.all);

/**
 * W1 `updateStation`: saves a change to one station (General Information, Hours of Operation,
 * Amenities, the logo in both headers, and the hidden Fuel details and Banking info).
 * What: sends the PATCH and resolves when the station data on screen is fresh again.
 * Cancels before: `stationKeys.all`.
 * Marks old after (all at once, so every screen that shows this station refreshes):
 * - `stationKeys.all`: the detail of this station, station lists, the picker searches, the
 *   portal's opening sites (names in the picker chips).
 * - `dashboardKeys.recentStation()`: the dashboard card of the recent station.
 * - `geofenceKeys.all`: zones and amenity locations feed the Heatmap and Zones, and a rename
 *   changes the site names in the visit data.
 * - `catalogKeys.all`: the Product Catalog filter options list the site names.
 * Why the detail is marked old and not written from the PATCH answer: the PATCH answer was never
 * read by any code, and the Zones card documents that a save answer can lack the amenity
 * `location`. Unproven, so the next read asks the server.
 * Constraint: it awaits the refresh of the station queries that are on screen, so the caller's
 * "saved" step (toast, form reset to the saved values) runs when the page already shows the saved
 * station. The other areas refresh in the background; their refresh errors never reject the save
 * (React Query swallows refetch errors of `invalidateQueries`).
 * Errors: not handled here; every caller shows its own feedback (`useDetailsToast` on the site
 * page) and logs.
 * Used by: `StationEntity` and `CustomerEntity` (the `update` of their contexts).
 */
export const useUpdateStation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: TUpdateStationPayload) =>
      updateStation(payload).then((response) => response.data.data),
    onMutate: () => cancelStationQueries(queryClient),
    onSuccess: () => {
      queryClient.invalidateQueries(dashboardKeys.recentStation());
      queryClient.invalidateQueries(geofenceKeys.all);
      queryClient.invalidateQueries(catalogKeys.all);

      return queryClient.invalidateQueries(stationKeys.all);
    },
  });
};

/**
 * Input of `useUploadStationLogo`: the image, the station, the current logo (to replace) and the
 * current general info (the PATCH sends the whole `generalInfo`, with only `logoUrl` changed).
 */
export type TUploadLogoVariables = {
  stationId: string;
  image: File;
  generalInfo: TStationInformation;
};

/**
 * W2 `uploadStationLogo` followed by W1: uploads the logo image, then saves the new image path in
 * the station's `generalInfo.logoUrl`.
 * What: resolves after both calls worked and the station on screen is fresh (see W1).
 * Why one hook: the logo only shows once the path is saved, so the two calls are one save. The
 * old image is passed as `replace` so the backend drops it.
 * Cancels before / marks old after: exactly W1 (`stationKeys.all`, recent station, geofence,
 * catalog). If the upload works but the PATCH fails, nothing is marked old (the station did not
 * change) and the caller gets the error.
 * Errors: not handled here; the logo modal and the page toast show them.
 * Used by: `StationEntityHeader` and `CustomerEntityHeader` (logo upload modal).
 */
export const useUploadStationLogo = () => {
  const { mutateAsync: update } = useUpdateStation();

  return useMutation({
    mutationFn: async ({ stationId, image, generalInfo }: TUploadLogoVariables) => {
      const { data } = await uploadStationLogo({
        stationId,
        replace: generalInfo.logoUrl || undefined,
        image,
      });

      return update({
        id: stationId,
        patch: { generalInfo: { ...generalInfo, logoUrl: data.data.imagePath } },
      });
    },
  });
};

/**
 * W3 `createStation`: creates a station (last real step of the Add Station wizard).
 * What: resolves to the created station (`response.data.data`).
 * Marks old after: `stationKeys.all` (station lists, picker searches, the portal's opening sites
 * and site total) and `dashboardKeys.recentStation()` (the new station can become the recent one).
 * Also tells `AuthProvider` the partner now has a station (`markHasStations`), so the first-run
 * welcome does not show again.
 * Constraint: the wizard calls this once per press; the caller must keep its submit disabled while
 * `isLoading`. Not awaited on purpose: the wizard moves on to the "complete" step at once.
 * Errors: not handled here; the wizard step shows the message and logs.
 * Used by: the wizard step `SystemConnection`.
 */
export const useCreateStation = () => {
  const queryClient = useQueryClient();
  const { markHasStations } = useAuth<true>();

  return useMutation({
    mutationFn: (payload: TCreateStationPayload) =>
      createStation(payload).then((response) => response.data.data),
    onSuccess: () => {
      markHasStations();
      queryClient.invalidateQueries(stationKeys.all);
      queryClient.invalidateQueries(dashboardKeys.recentStation());
    },
  });
};
